import { useEffect, useState } from "react"

type SheetPanelHeightOptions = {
    enabled: boolean
    /** Доля видимой высоты экрана (0.92 = 92%) */
    ratio?: number
    /** Только при открытой клавиатуре (compact auth) */
    onlyWhenKeyboard?: boolean
}

/** Высота bottom sheet в px по visualViewport — одинаково во всех mobile-браузерах */
export function useSheetPanelHeight({
    enabled,
    ratio = 0.92,
    onlyWhenKeyboard = false,
}: SheetPanelHeightOptions): number | null {
    const [height, setHeight] = useState<number | null>(null)

    useEffect(() => {
        if (!enabled) {
            setHeight(null)
            return
        }

        const vv = window.visualViewport
        if (!vv) return

        const update = () => {
            const keyboardLikelyOpen = vv.height < window.innerHeight * 0.8

            if (onlyWhenKeyboard && !keyboardLikelyOpen) {
                setHeight(null)
                return
            }

            setHeight(Math.round(vv.height * ratio))
        }

        vv.addEventListener("resize", update)
        vv.addEventListener("scroll", update)
        window.addEventListener("orientationchange", update)
        update()

        return () => {
            vv.removeEventListener("resize", update)
            vv.removeEventListener("scroll", update)
            window.removeEventListener("orientationchange", update)
        }
    }, [enabled, ratio, onlyWhenKeyboard])

    return height
}

/**
 * Высота клавиатуры в px (0 — закрыта). На iOS layout viewport не сжимается
 * при открытии клавиатуры, поэтому fixed-элементы уходят под неё — этим
 * значением можно приподнять bottom sheet. Учитывает панорамирование
 * (visualViewport.offsetTop), чтобы не компенсировать сдвиг дважды.
 */
export function useKeyboardInset(enabled: boolean): number {
    const [inset, setInset] = useState(0)

    useEffect(() => {
        if (!enabled) {
            setInset(0)
            return
        }

        const vv = window.visualViewport
        if (!vv) return

        const update = () => {
            const value = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
            // Порог отсекает мелкие изменения браузерного UI (адресная строка и т.п.)
            setInset(value > 80 ? value : 0)
        }

        vv.addEventListener("resize", update)
        vv.addEventListener("scroll", update)
        update()

        return () => {
            vv.removeEventListener("resize", update)
            vv.removeEventListener("scroll", update)
        }
    }, [enabled])

    return inset
}

/** @deprecated используйте useSheetPanelHeight */
export function useVisualViewportMaxHeight(enabled: boolean) {
    return useSheetPanelHeight({ enabled, ratio: 1, onlyWhenKeyboard: true })
}
